/**
 * Single-organization detail hook using React Relay.
 *
 * Fetches one organization (by id) together with its parent organization.
 * Used by the org Settings General tab to populate the "Parent Organization"
 * field, and by the sidebar to decide whether to show the Branded App section.
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useState, useEffect, useCallback } from 'react';
import { fetchQuery } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type {
  useOrganizationDetailRelayQuery as UseOrganizationDetailRelayQuery,
  useOrganizationDetailRelayQuery$variables,
} from './__generated__/useOrganizationDetailRelayQuery.graphql';

const OrganizationDetailQuery = graphql`
  query useOrganizationDetailRelayQuery($id: ID!) {
    getOrganization(id: $id) {
      id
      name
      hasBrandedApp
      parent {
        id
        name
      }
    }
  }
`;

export interface OrganizationDetailGraphQL {
  id: string;
  name: string;
  /** True when the organization's partner has a branded rider app. */
  hasBrandedApp: boolean | null;
  parent: {
    id: string;
    name: string;
  } | null;
}

/**
 * Hook that provides a React Query-like interface for a single organization.
 *
 * Does NOT use Suspense - provides isLoading, data, refetch etc.
 *
 * @example
 * const { data, isLoading } = useOrganizationDetailRelayQuery(orgId);
 */
export function useOrganizationDetailRelayQuery(
  id?: string | null,
  options?: { enabled?: boolean }
) {
  const [data, setData] = useState<OrganizationDetailGraphQL | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0); // For manual refetch trigger

  const enabled = options?.enabled !== false && Boolean(id);

  useEffect(() => {
    if (!enabled || !id) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        setIsLoading(true);

        const variables: useOrganizationDetailRelayQuery$variables = { id };

        const result = await fetchQuery<UseOrganizationDetailRelayQuery>(
          relayEnvironment,
          OrganizationDetailQuery,
          variables
        ).toPromise();

        if (cancelled) return;

        if (result) {
          setData((result.getOrganization as OrganizationDetailGraphQL) ?? null);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error('Failed to fetch organization'));
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, id, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return {
    data,
    isLoading,
    isFetched,
    error,
    refetch,
  };
}
